// The confidence history of one Issue, turned into something drawable. Pure: no
// Angular, no clock of its own, so the shapes it produces can be asserted directly.
//
// The stored history is not evenly sampled, and the chart must not pretend it is.
// A discrete row is one real value at one real moment and draws as a point. An
// aging run folds many decay passes into a single row, so it draws as a straight
// segment from where the run started to where it ended. The row records nothing of
// the shape between, so the chart invents none.

import { IssueDto, IssueLifecycleStates, OPEN_LIFECYCLE_STATES } from '../../models/issue';
import { MetricSnapshotDto, SnapshotTriggers } from '../../models/metric-snapshot';
import { isUnsetDate } from '../../issue-formatting';

export interface ConfidenceSegmentVm {
  fromMs: number;
  fromConfidence: number;
  toMs: number;
  toConfidence: number;
  // How many aging passes this segment stands for. 0 for a plain connector
  // between two discrete samples.
  sampleCount: number;
}

export interface ConfidencePointVm {
  atMs: number;
  confidence: number;
  trigger: string;
  lifecycleState: string;
  severity: number;
}

export interface SeverityStepVm {
  fromMs: number;
  toMs: number;
  severity: number;
}

export interface ConfidenceSeriesVm {
  segments: ConfidenceSegmentVm[];
  points: ConfidencePointVm[];
  severitySteps: SeverityStepVm[];
  startMs: number;
  endMs: number;
  startConfidence: number;
  endConfidence: number;
  // False when every sample asserts nothing, which is the closed-and-spent case.
  // The ribbon is then all one grey and earns none of its height.
  hasSeverity: boolean;
}

// One row of the stored history, read into the two shapes the walk below needs:
// an instant, or a run between two instants.
interface SeriesNode {
  startMs: number;
  startConfidence: number;
  endMs: number;
  endConfidence: number;
  sampleCount: number;
  trigger: string;
  severity: number;
  lifecycleState: string;
  isRun: boolean;
}

function clampConfidence(value: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

function clampSeverity(value: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(n)));
}

function toNode(row: MetricSnapshotDto): SeriesNode | null {
  if (!row || isUnsetDate(row.capturedAt)) return null;
  const endMs = Date.parse(row.capturedAt);
  const endConfidence = clampConfidence(row.confidence);
  const severity = clampSeverity(row.severity);
  const trigger = row.trigger ?? '';
  const lifecycleState = row.lifecycleState ?? '';

  const runStartMs = isUnsetDate(row.firstCapturedAt) ? NaN : Date.parse(row.firstCapturedAt);
  const isRun = trigger === SnapshotTriggers.AgingSummary
    && Number.isFinite(runStartMs)
    && runStartMs < endMs;

  if (!isRun) {
    return {
      startMs: endMs, startConfidence: endConfidence,
      endMs, endConfidence,
      sampleCount: 0, trigger, severity, lifecycleState, isRun: false,
    };
  }

  const startConfidence = clampConfidence(row.confidenceStart);
  return {
    startMs: runStartMs, startConfidence,
    endMs, endConfidence,
    sampleCount: Math.max(1, Math.round(Number(row.sampleCount) || 1)),
    trigger, severity, lifecycleState, isRun: true,
  };
}

function connector(
  fromMs: number, fromConfidence: number, toMs: number, toConfidence: number,
): ConfidenceSegmentVm {
  return {
    fromMs, fromConfidence, toMs, toConfidence,
    sampleCount: 0,
  };
}

// The spans the Issue spent closed, from the state history. A close that an operator
// made writes no confidence sample, so the snapshots alone cannot place it; the history
// always can. A transition into Closed opens a span and the next transition out of it
// ends one. A close with no reopen after it runs to the end of time, so the tail of the
// line can find it.
function closedSpansOf(issue: IssueDto): { fromMs: number; toMs: number }[] {
  const history = (issue.stateHistory ?? [])
    .filter(h => h && !isUnsetDate(h.transitionedAt))
    .map(h => ({ ...h, ms: Date.parse(h.transitionedAt) }))
    .sort((a, b) => a.ms - b.ms);

  const spans: { fromMs: number; toMs: number }[] = [];
  let openSince: number | null = null;
  for (const h of history) {
    if (h.toState === IssueLifecycleStates.Closed && openSince === null) openSince = h.ms;
    else if (h.fromState === IssueLifecycleStates.Closed && openSince !== null) {
      if (h.ms > openSince) spans.push({ fromMs: openSince, toMs: h.ms });
      openSince = null;
    }
  }
  if (openSince !== null) spans.push({ fromMs: openSince, toMs: Number.POSITIVE_INFINITY });
  return spans;
}

// A dead Issue reads as dead. Between two samples that bracket a closed span the line
// goes to the floor at the close, lies along it while the Issue is closed, and rises
// straight up at the reopen. A plain connector across that time drew a slow climb that
// never happened.
function acrossClose(
  prevMs: number, prevConfidence: number, toMs: number, toConfidence: number,
  span: { fromMs: number; toMs: number },
): ConfidenceSegmentVm[] {
  const closeMs = Math.max(prevMs, span.fromMs);
  const reopenMs = Math.min(toMs, Math.max(closeMs, span.toMs));
  const out: ConfidenceSegmentVm[] = [];
  if (closeMs > prevMs || prevConfidence !== 0) out.push(connector(prevMs, prevConfidence, closeMs, 0));
  if (reopenMs > closeMs) out.push(connector(closeMs, 0, reopenMs, 0));
  out.push(connector(reopenMs, 0, reopenMs, toConfidence));
  if (toMs > reopenMs) out.push(connector(reopenMs, toConfidence, toMs, toConfidence));
  return out;
}

// The ribbon steps where the history proves severity changed. A rise always has a
// discrete row of its own, because severity only rises when a signal arrives. A
// fall happens inside an aging run, and the run records only the value it ended on,
// so the step lands at the end of the run rather than at the moment the contribution
// went quiet. A step holds until the next one, so the last value of a closed Issue
// covers no time at all and the ribbon ends where the history ends.
function buildSeveritySteps(nodes: SeriesNode[], startMs: number, endMs: number): SeverityStepVm[] {
  if (!nodes.length) return [];

  const marks = nodes.map(n => ({ ms: n.isRun ? n.endMs : n.startMs, severity: n.severity }));
  marks[0].ms = Math.min(marks[0].ms, startMs);

  const steps: SeverityStepVm[] = [];
  for (const mark of marks) {
    const open = steps[steps.length - 1];
    if (open && open.severity === mark.severity) continue;
    if (open) open.toMs = Math.max(open.fromMs, mark.ms);
    steps.push({ fromMs: mark.ms, toMs: endMs, severity: mark.severity });
  }
  const last = steps[steps.length - 1];
  if (last) last.toMs = Math.max(last.fromMs, endMs);
  return steps.filter(s => s.toMs > s.fromMs);
}

// nowUtcMs is passed in rather than read, so a test can place "now" where it needs it.
export function buildConfidenceSeries(
  issue: IssueDto | null | undefined,
  nowUtcMs: number,
): ConfidenceSeriesVm | null {
  if (!issue) return null;

  const nodes = (issue.metricSnapshots ?? [])
    .map(toNode)
    .filter((n): n is SeriesNode => n !== null)
    .sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);

  if (!nodes.length) return null;

  const segments: ConfidenceSegmentVm[] = [];
  const points: ConfidencePointVm[] = [];
  let prev: { ms: number; confidence: number } | null = null;
  const closedSpans = closedSpansOf(issue);
  // The reopen sample and the reopen transition share an instant to the second, not
  // always to the millisecond.
  const slack = 60_000;
  const spanBetween = (fromMs: number, toMs: number) =>
    closedSpans.find(c => c.fromMs >= fromMs - slack && c.toMs <= toMs + slack && c.fromMs < toMs && c.toMs > fromMs) ?? null;

  // The line only ever moves forward. Rows can overlap, and a clock that ran backwards
  // would otherwise fold the line over itself and read as a spike that never happened.
  // A sample the line has already passed still draws its own point, at the moment it
  // was taken, because that much is known; it just does not drag the line back.
  for (const node of nodes) {
    if (!node.isRun) {
      points.push({
        atMs: node.endMs,
        confidence: node.endConfidence,
        trigger: node.trigger,
        lifecycleState: node.lifecycleState,
        severity: node.severity,
      });

      if (prev && node.endMs < prev.ms) continue;
      if (prev && (node.endMs > prev.ms || node.endConfidence !== prev.confidence)) {
        const span = spanBetween(prev.ms, node.endMs);
        if (span) segments.push(...acrossClose(prev.ms, prev.confidence, node.endMs, node.endConfidence, span));
        else segments.push(connector(prev.ms, prev.confidence, node.endMs, node.endConfidence));
      }
      prev = { ms: node.endMs, confidence: node.endConfidence };
      continue;
    }

    const startMs = prev ? Math.max(node.startMs, prev.ms) : node.startMs;
    if (node.endMs <= startMs) continue;

    if (prev && (startMs > prev.ms || node.startConfidence !== prev.confidence)) {
      segments.push(connector(prev.ms, prev.confidence, startMs, node.startConfidence));
    }

    segments.push({
      fromMs: startMs,
      fromConfidence: node.startConfidence,
      toMs: node.endMs,
      toConfidence: node.endConfidence,
      sampleCount: node.sampleCount,
    });

    prev = { ms: node.endMs, confidence: node.endConfidence };
  }

  // An open Issue keeps moving after its last stored row: an aging pass writes only
  // when the value moved enough to be worth a row. The line runs on to the value the
  // header is showing, so the chart and the header cannot disagree. A closed Issue
  // stops where it stopped.
  const isOpen = OPEN_LIFECYCLE_STATES.includes(issue.lifecycleState);
  if (prev && isOpen && nowUtcMs > prev.ms) {
    const current = clampConfidence(issue.confidence);
    segments.push(connector(prev.ms, prev.confidence, nowUtcMs, current));
    prev = { ms: nowUtcMs, confidence: current };
  }

  // A closed Issue whose close wrote no sample would otherwise end on its last value, as
  // if it were still alive. The history places the close, and the line goes to the floor
  // there and stops.
  if (prev && !isOpen) {
    const last = prev;
    const tail = closedSpans.find(c => c.toMs === Number.POSITIVE_INFINITY && c.fromMs >= last.ms - slack);
    if (tail) {
      const closeMs = Math.max(last.ms, tail.fromMs);
      if (closeMs > last.ms || last.confidence !== 0) segments.push(connector(last.ms, last.confidence, closeMs, 0));
      prev = { ms: closeMs, confidence: 0 };
    }
  }

  const startMs = nodes[0].startMs;
  const endMs = prev ? prev.ms : nodes[nodes.length - 1].endMs;
  // One instant is not a history. There is nothing to draw across, and a lone dot
  // reads as a fault.
  if (endMs <= startMs) return null;

  return {
    segments,
    points,
    severitySteps: buildSeveritySteps(nodes, startMs, endMs),
    startMs,
    endMs,
    startConfidence: nodes[0].startConfidence,
    endConfidence: prev ? prev.confidence : nodes[nodes.length - 1].endConfidence,
    hasSeverity: nodes.some(n => n.severity > 0),
  };
}
